import type { IconName } from "@similarweb/icons";

import { translate } from "app/filters/i18nFilter";
import type {
    BaseActivatedPanelProps,
    SelectionDropdown,
    SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    BaseActivatedPanel,
    ManualSelectionItem,
    SelectionDDItem,
    SelectionDropdownFooter,
    SelectionDropdownView,
    StyledPanelInner,
    useActionsPanelState,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import PanelSubmitIconButton from "app/pages/sales-intelligence/common-components/table-actions-panel/components/PanelSubmitIconButton/PanelSubmitIconButton";
import PanelTitleWithIcon from "app/pages/sales-intelligence/common-components/table-actions-panel/components/PanelTitleWithIcon/PanelTitleWithIcon";
import type { Translatable } from "app/pages/sales-intelligence/types";

import type { OpportunityListType } from "../../../types";
import AddToListDropdownContainer from "../../miscellaneous/AddToListDropdown/AddToListDropdownContainer";

export type SaveToListPanelProps = {
    isLoading?: boolean;
    isDisabled?: boolean;
    isSubmitInProgress: boolean;
    numberOfRemainingItems: number;
    numberOfResults: number;
    numberOfSelectedItems: number;
    numberOfUntrackedSelectedItems: number;
    selectionDropdown: SelectionDropdown;
    onSubmit: (list: OpportunityListType, selectionItem: SelectionDropdownItem) => void | Promise<void>;
    onSelectionChange: (item: SelectionDropdownItem) => void;
    lists?: OpportunityListType[];
    panelTitleIconName?: IconName;
    panelTitleLabel?: Readonly<Translatable>;
} & BaseActivatedPanelProps;

const SaveToListPanel = (props: SaveToListPanelProps) => {
    const {
        className,
        isLoading = false,
        isDisabled = false,
        isSubmitInProgress,
        numberOfRemainingItems,
        numberOfResults,
        numberOfSelectedItems,
        numberOfUntrackedSelectedItems,
        selectionDropdown,
        onSelectionChange,
        onSubmit,
        lists,
        panelTitleIconName = "add-to-list",
        panelTitleLabel = { key: "si.multi_selector.account.addToListTitle" },
    } = props;
    const state = useActionsPanelState(selectionDropdown, {
        numberOfResults,
        numberOfSelectedItems,
        onSelectionChange,
        limit: numberOfRemainingItems,
    });

    const isManualSelectionOverQuota =
        state.activeSelectionItem.id === ManualSelectionItem.ID &&
        numberOfUntrackedSelectedItems > numberOfRemainingItems;

    function handleListSelect(list: Readonly<OpportunityListType>) {
        void onSubmit(list, state.activeSelectionItem);
    }

    return (
        <BaseActivatedPanel
            className={className}
            onDeactivate={props.onDeactivate}
        >
            <StyledPanelInner>
                <PanelTitleWithIcon
                    iconName={panelTitleIconName}
                    title={panelTitleLabel}
                />
                <SelectionDropdownView
                    buttonText={state.activeSelectionItem.getSelectedTranslation()}
                    isDisabled={isSubmitInProgress || isDisabled}
                    isLoading={isLoading}
                    footer={
                        <SelectionDropdownFooter
                            label={translate("si.multi_selector.account.dropdown.bottom.label")}
                            numberOfRemainingItems={numberOfRemainingItems}
                        />
                    }
                    onSelect={state.setActiveSelectionItem}
                >
                    {state.dropdownItems
                        .map((item) => (
                            <SelectionDDItem
                                key={item.id}
                                isSelected={state.activeSelectionItem.id === item.id}
                                item={item}
                            />
                        ))
                        .concat(
                            <SelectionDDItem
                                isSelected={state.activeSelectionItem.id === ManualSelectionItem.ID}
                                item={state.manualSelectionItem}
                                key={ManualSelectionItem.ID}
                            />,
                        )}
                </SelectionDropdownView>
                <AddToListDropdownContainer
                    domains={[]}
                    opportunityList={lists}
                    labelCreateBtn={translate("si.common.button.create")}
                    dropdownItemClick={handleListSelect}
                    onFinishedCreatingList={handleListSelect}
                    renderDropdownButton={(openDropdown) => (
                        <PanelSubmitIconButton
                            isDisabled={state.isSelectionEmpty || isManualSelectionOverQuota || isDisabled}
                            isLoading={isSubmitInProgress}
                            dataAutomation="multi-selector.select-list-button"
                            text={translate("si.multi_selector.button.select_list.label")}
                            tooltipContent={translate(
                                isManualSelectionOverQuota
                                    ? "si.multi_selector.tooltip.save-to-list.not_enough_quota"
                                    : "si.multi_selector.tooltip.account.unselected_domains",
                            )}
                            onClick={openDropdown}
                        />
                    )}
                />
            </StyledPanelInner>
        </BaseActivatedPanel>
    );
};

export default SaveToListPanel;
